import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {};

const BASE_URL = "/api";

type Industry = "美容医療" | "エステティック" | "異業種" | "BtoB" | "その他";

type ProductsData = {
  merchant_id: string;
  continuous_rate: string;
  continuous_max_days: string;
  continuous_treatment_name: string;
  non_continuous_rate: string;
  non_continuous_max_days_after_contract: string;
  non_continuous_treatment_name: string;
  goods_rate: string;
  supplier: string;
  goods_type: string;
  b2b_goods: string;
  b2b_delivery_days_after_contract: string;
  price_range_select: string;
  price_range_text: string;
  is_completed: boolean;
  updated_at?: string;
};

type FieldDef = { key: keyof ProductsData; label: string; placeholder?: string };

const getStaffAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("staff_google_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function EditRow({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "260px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          outline: "none",
          fontWeight: 700,
          color: "#0f172a",
          background: "#fff",
        }}
      />
    </div>
  );
}

function ReadonlyInputBox({
  label,
  value,
  required,
  suffix,
}: {
  label: string;
  value: string | undefined | null;
  required?: boolean;
  suffix?: string;
}) {
  const displayValue = value && String(value).trim() ? String(value) : "—";

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          position: "relative",
          width: "100%",
          height: 44,
          padding: suffix ? "0 70px 0 12px" : "0 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          fontWeight: 700,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {displayValue}

        {suffix && (
          <span
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#475569",
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function SectionBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 12,
        background: "#ffffff",
      }}
    >
      <div style={{ fontWeight: 1000, marginBottom: 10, color: "#0f172a" }}>{title}</div>
      {children}
    </div>
  );
}

function ThreeCol({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
        gap: 20,
        alignItems: "start",
      }}
    >
      {children}
    </div>
  );
}

export default function StaffFormViewProducts() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ProductsData | null>(null);
  const [draft, setDraft] = useState<ProductsData | null>(null);
  const [error, setError] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [industry, setIndustry] = useState<Industry>("その他");

  const isBtoB = industry === "BtoB";
  const showNonContinuousBlock = !isBtoB && industry !== "エステティック";
  const showMainProductsSection = !isBtoB;
  const showBtoBSection = isBtoB;

  const fields: FieldDef[] = useMemo(
    () => [
      { key: "continuous_rate", label: "継続役務：解約料（％）" },
      { key: "continuous_max_days", label: "継続役務：最大日数" },
      { key: "continuous_treatment_name", label: "継続役務：施術名" },
      { key: "non_continuous_rate", label: "非継続役務：解約料（％）" },
      { key: "non_continuous_max_days_after_contract", label: "非継続役務：契約後の最大日数" },
      { key: "non_continuous_treatment_name", label: "非継続役務：施術名" },
      { key: "goods_rate", label: "物販：解約料（％）" },
      { key: "supplier", label: "物販：仕入先" },
      { key: "goods_type", label: "物販：商品区分" },
      { key: "b2b_goods", label: "BtoB物販：有無/内容" },
      { key: "b2b_delivery_days_after_contract", label: "BtoB物販：契約後の納品日数" },
      { key: "price_range_select", label: "取扱金額（選択）" },
      { key: "price_range_text", label: "取扱金額（自由記述）" },
    ],
    []
  );

  const fetchData = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");

    try {
      const progressRes = await fetch(`${BASE_URL}/staff/progress/${merchantId}`, {
        headers: getStaffAuthHeaders(),
      });
      const progressJson = await progressRes.json().catch(() => ({}));

      if (progressRes.ok) {
        const rawIndustry = String(progressJson?.merchant?.industry || "").trim();
        if (
          rawIndustry === "美容医療" ||
          rawIndustry === "エステティック" ||
          rawIndustry === "異業種" ||
          rawIndustry === "BtoB" ||
          rawIndustry === "その他"
        ) {
          setIndustry(rawIndustry as Industry);
        } else {
          setIndustry("その他");
        }
      }

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form4_products`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const d: ProductsData = {
        merchant_id: merchantId,
        continuous_rate: json?.data?.continuous_rate || "",
        continuous_max_days: json?.data?.continuous_max_days || "",
        continuous_treatment_name: json?.data?.continuous_treatment_name || "",
        non_continuous_rate: json?.data?.non_continuous_rate || "",
        non_continuous_max_days_after_contract: json?.data?.non_continuous_max_days_after_contract || "",
        non_continuous_treatment_name: json?.data?.non_continuous_treatment_name || "",
        goods_rate: json?.data?.goods_rate || "",
        supplier: json?.data?.supplier || "",
        goods_type: json?.data?.goods_type || "",
        b2b_goods: json?.data?.b2b_goods || "",
        b2b_delivery_days_after_contract: json?.data?.b2b_delivery_days_after_contract || "",
        price_range_select: json?.data?.price_range_select || "",
        price_range_text: json?.data?.price_range_text || "",
        is_completed: !!json?.data?.is_completed,
        updated_at: json?.data?.updated_at,
      };

      setData(d);
      setDraft(d);
      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム④（取扱商品）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    if (!merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム4_取扱商品_${merchantId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const saveEdits = async () => {
    if (!merchantId) return;
    if (!draft) return;

    try {
      setSaving(true);
      setError("");

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form4_products`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          continuous_rate: draft.continuous_rate,
          continuous_max_days: draft.continuous_max_days,
          continuous_treatment_name: draft.continuous_treatment_name,
          non_continuous_rate: draft.non_continuous_rate,
          non_continuous_max_days_after_contract: draft.non_continuous_max_days_after_contract,
          non_continuous_treatment_name: draft.non_continuous_treatment_name,
          goods_rate: draft.goods_rate,
          supplier: draft.supplier,
          goods_type: draft.goods_type,
          b2b_goods: draft.b2b_goods,
          b2b_delivery_days_after_contract: draft.b2b_delivery_days_after_contract,
          price_range_select: draft.price_range_select,
          price_range_text: draft.price_range_text,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.detail || "save failed");

      setEditMode(false);
      await fetchData();
      alert("保存しました。");
    } catch (e: any) {
      setError(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffLayout title="フォーム④ 取扱商品（確認・編集）" active="progress">
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        <button
          onClick={() => setEditMode((v) => !v)}
          disabled={loading || !!error || !data}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: editMode ? "#fff" : "#0b1f3b",
            color: editMode ? "#0b1f3b" : "#fff",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          {editMode ? "編集をやめる" : "編集する（スタッフ）"}
        </button>

        {editMode && (
          <button
            onClick={saveEdits}
            disabled={saving || !draft}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #16a34a",
              background: saving ? "#14532d" : "#16a34a",
              color: "#fff",
              fontWeight: 900,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "保存中..." : "保存"}
          </button>
        )}
      </div>

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data.is_completed ? "#166534" : "#9a3412" }}>
              {data.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              入力内容（{editMode ? "編集モード（スタッフ）" : "確認"}）
            </div>

            {!editMode && (
              <div style={{ padding: 12 }}>
                <div style={{ marginBottom: 10, display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ fontSize: 13, color: "#475569", fontWeight: 900 }}>業種：</div>
                  <div
                    style={{
                      background: "#0b1f3b",
                      color: "#fff",
                      fontWeight: 900,
                      fontSize: 12,
                      padding: "6px 10px",
                      borderRadius: 999,
                    }}
                  >
                    {industry}
                  </div>
                </div>

                {showMainProductsSection && (
                  <div
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px",
                        fontWeight: 900,
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      取扱商品
                    </div>

                    <div style={{ padding: 12, display: "grid", gap: 12 }}>
                      <SectionBox title="特定継続的役務">
                        <ThreeCol>
                          <ReadonlyInputBox label="特定継続的役務" value={data.continuous_rate} required suffix="%" />
                          <ReadonlyInputBox label="最長役務期間" value={data.continuous_max_days} required suffix="日" />
                          <ReadonlyInputBox label="具体的な施術名" value={data.continuous_treatment_name} required />
                        </ThreeCol>
                      </SectionBox>

                      {showNonContinuousBlock && (
                        <SectionBox title="特定継続的役務外">
                          <ThreeCol>
                            <ReadonlyInputBox label="特定継続的役務外" value={data.non_continuous_rate} required suffix="%" />
                            <ReadonlyInputBox
                              label="最長施術日程"
                              value={data.non_continuous_max_days_after_contract}
                              required
                              suffix="日後"
                            />
                            <ReadonlyInputBox label="具体的な施術名" value={data.non_continuous_treatment_name} required />
                          </ThreeCol>
                        </SectionBox>
                      )}

                      <SectionBox title="商品">
                        <ThreeCol>
                          <ReadonlyInputBox label="商品" value={data.goods_rate} required suffix="%" />
                          <ReadonlyInputBox label="仕入れ先" value={data.supplier} required />
                          <ReadonlyInputBox label="商品種類" value={data.goods_type} required />
                        </ThreeCol>
                      </SectionBox>
                    </div>
                  </div>
                )}

                {showBtoBSection && (
                  <div
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px",
                        fontWeight: 900,
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      BtoB（必須）
                    </div>

                    <div style={{ padding: 12 }}>
                      <ThreeCol>
                        <ReadonlyInputBox label="取扱商品" value={data.b2b_goods} required />
                        <ReadonlyInputBox label="納品時期" value={data.b2b_delivery_days_after_contract} required suffix="日後" />
                        <ReadonlyInputBox label="価格帯" value={data.price_range_select} required />
                      </ThreeCol>

                      {data.price_range_select === "その他（自由入力）" && (
                        <div style={{ marginTop: 12 }}>
                          <ReadonlyInputBox label="価格帯（自由入力）" value={data.price_range_text} required />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {editMode &&
              draft &&
              fields.map((f) => (
                <EditRow
                  key={String(f.key)}
                  label={f.label}
                  value={String((draft as any)[f.key] ?? "")}
                  onChange={(v) => setDraft({ ...draft, [f.key]: v } as ProductsData)}
                />
              ))}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
